import { NavLink, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '../../utils/cn'
import type { DashboardUserRole } from '../../lib/api/auth'
import { dashboardRouteGroups, DashboardRoute, DashboardRouteGroup, getDashboardRouteContext, getDashboardRouteGroupsForRole } from './nav-items'
import { useAuth } from '../../hooks/useAuth'

interface DashboardNavProps {
  isCollapsed?: boolean
  onNavigate?: () => void
}

export const DashboardNav = ({ isCollapsed = false, onNavigate }: DashboardNavProps) => {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const role = typeof user?.role === 'string' ? user.role as DashboardUserRole : undefined
  const activeRoute = getDashboardRouteContext(pathname, role)
  const filteredGroups = useMemo<DashboardRouteGroup[]>(() => {
    return getDashboardRouteGroupsForRole(role)
  }, [role])

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') {
      return {}
    }

    const stored = window.localStorage.getItem('hikestrong-dashboard-nav-groups')
    if (stored) {
      try {
        return JSON.parse(stored) as Record<string, boolean>
      } catch {
        // Ignore parse errors and fall back to defaults.
      }
    }

    return dashboardRouteGroups.reduce<Record<string, boolean>>((acc, group) => {
      acc[group.id] = group.defaultExpanded ?? false
      return acc
    }, {})
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.setItem('hikestrong-dashboard-nav-groups', JSON.stringify(expandedGroups))
  }, [expandedGroups])

  const renderRoute = (route: DashboardRoute) => {
    const Icon = route.icon
    return (
      <NavLink
        key={route.path}
        to={route.path}
        end={activeRoute?.path !== route.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'group relative flex min-h-10 items-center gap-3 rounded-lg text-[13px] font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#183d32]',
            isActive
              ? 'bg-white/15 text-white'
              : 'text-white/70 hover:bg-white/[0.07] hover:text-white',
            isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
          )
        }
        title={isCollapsed ? route.label : undefined}
      >
        {({ isActive }) => (
          <>
            {isActive && !isCollapsed && (
              <span aria-hidden="true" className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-r bg-[#d0e8c5]" />
            )}
            <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.7} />
            {isCollapsed ? <span className="sr-only">{route.label}</span> : <span className="min-w-0 flex-1">{route.label}</span>}
          </>
        )}
      </NavLink>
    )
  }

  const flatRoutes = useMemo(() => filteredGroups.flatMap((group) => group.items), [filteredGroups])

  return (
    <nav aria-label="Main navigation" className={cn('flex flex-col', isCollapsed ? 'gap-1' : 'gap-5')}>
      {isCollapsed
        ? flatRoutes.map((route) => renderRoute(route))
        : filteredGroups.map((group) => {
            const isExpanded = expandedGroups[group.id] ?? group.defaultExpanded ?? false
            return (
              <div key={group.id} className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [group.id]: !(prev[group.id] ?? group.defaultExpanded ?? false)
                    }))
                  }
                  className={cn(
                    'flex min-h-8 items-center justify-between rounded-md px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/55',
                    'hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
                  )}
                  aria-expanded={isExpanded}
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    className={cn(
                      'h-3 w-3 transition-transform duration-200 motion-reduce:transition-none',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {isExpanded && (
                  <div className="flex flex-col gap-0.5">
                    {group.items.map((route) => renderRoute(route))}
                  </div>
                )}
              </div>
            )
          })}
    </nav>
  )
}
